import get from 'lodash/get';

import { getWidgetBodyTranslations } from '@lumapps/designer-translations/utils/getWidgetBodyTranslations';
import { WIDGET_TITLE_TYPE } from '@lumapps/widget-title/constants';

import type { WidgetPathsMap } from './getAllWidgetsWithPaths';

interface WidgetDirtyTextualFields {
    /** Dirty languages for widget header */
    header?: Record<string, boolean>;
    /** Dirty languages for widget body */
    body?: Record<string, boolean>;
    /** Dirty languages for widget footer */
    footer?: Record<string, boolean>;
}

/** Map of widgetId → dirty textual fields. Only contains widgets with at least one dirty textual field. */
export type WidgetsDirtyTextualFields = Record<string, WidgetDirtyTextualFields>;

/**
 * Compute which widget textual fields (header, body, footer) are dirty, per widget and per language.
 *
 * @param widgetPaths - Map of widgetId → { path, widgetType } from ContentEditorContext
 * @param dirtyFields - formState.dirtyFields from react-hook-form
 * @returns Map of widgetId → { header, body, footer } with dirty languages per field.
 *          Only includes widgets that have at least one dirty textual field.
 */
export const getWidgetsDirtyTextualFields = (
    widgetPaths: WidgetPathsMap,
    dirtyFields: Record<string, any>,
): WidgetsDirtyTextualFields => {
    // No template or title changes — skip everything
    if (!dirtyFields.template && !dirtyFields.title) {
        return {};
    }

    const result: WidgetsDirtyTextualFields = {};

    for (const [widgetId, { path, widgetType }] of Object.entries(widgetPaths)) {
        const widgetDirty = get(dirtyFields, path);
        const isTitleWidget = widgetType === WIDGET_TITLE_TYPE;

        if (widgetDirty || isTitleWidget) {
            const headerDirty = widgetDirty?.title;
            const bodyKey = getWidgetBodyTranslations(widgetType, {})?.key;
            const bodyDirty = bodyKey ? get(widgetDirty?.properties, bodyKey) : undefined;
            const footerDirty = widgetDirty?.properties?.more?.label;

            // Title widget body is the content title at root level, not on the widget path.
            const effectiveBodyDirty = isTitleWidget ? dirtyFields.title : bodyDirty;

            if (headerDirty || effectiveBodyDirty || footerDirty) {
                result[widgetId] = {
                    header: headerDirty,
                    body: effectiveBodyDirty,
                    footer: footerDirty,
                };
            }
        }
    }

    return result;
};
